<template>
    <!-- 站内页面的面包屑显示在 header 第三行；弹窗等无 header 的页面仍原地显示 -->
    <Teleport defer to="#header-breadcrumb" :disabled="!inHeader">
    <span class="page-title-path">
        <template v-if="items.length" v-for="(item, index) in items" :key="`${item}-${index}`">
            <RouterLink v-if="!inHeader && homePath && index === 0 && items.length > 1" class="page-title-item is-link"
                :to="homePath" :title="item">
                {{ item }}
            </RouterLink>
            <el-dropdown v-else-if="index === items.length - 1 && submenuOptions.length" trigger="click"
                popper-class="page-title-dropdown"
                @command="navigateToSubmenu">
                <button type="button" class="page-title-item is-current page-title-switch">
                    <span>{{ item }}</span><el-icon><ArrowDown /></el-icon>
                </button>
                <template #dropdown>
                    <el-dropdown-menu>
                        <el-dropdown-item v-for="option in submenuOptions" :key="option.path" :command="option.path"
                            :class="{ 'is-active': isCurrentOption(option.path) }">
                            {{ option.label }}
                        </el-dropdown-item>
                    </el-dropdown-menu>
                </template>
            </el-dropdown>
            <span v-else :class="['page-title-item', { 'is-current': index === items.length - 1 }]" :title="item">
                {{ item }}
            </span>
            <span v-if="index < items.length - 1" class="page-title-separator" aria-hidden="true">›</span>
        </template>
        <span v-else class="page-title-item is-current">
            <slot>{{ fallback }}</slot>
        </span>
    </span>
    </Teleport>
    <span v-if="inHeader" class="page-title-teleported" hidden></span>
</template>

<script setup>
import { computed, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useNavigationStore } from '@/stores/navigationStore'
import { useUserStore } from '@/stores/user'
import { ArrowDown } from '@element-plus/icons-vue'

const props = defineProps({
    fallback: {
        type: String,
        default: '',
    },
    fallbackItems: {
        type: Array,
        default: () => [],
    },
    homePath: {
        type: String,
        default: '',
    },
})

const route = useRoute()
const router = useRouter()
// DefaultLayout 提供：header 里有面包屑容器
const inHeader = inject('headerBreadcrumb', false)
const userStore = useUserStore()
const { t } = useI18n()
const navigationStore = useNavigationStore()
const submenuOptions = computed(() => navigationStore.routePath === route.path
    ? navigationStore.submenuOptions : [])
const isCurrentOption = (path) => {
    const destination = router.resolve(path)
    return destination.path === route.path && Object.entries(destination.query)
        .filter(([key]) => key !== 'title')
        .every(([key, value]) => String(route.query[key] ?? '') === String(value ?? ''))
}
const navigateToSubmenu = (path) => {
    const destination = !userStore.userInfo?.token && !router.resolve(path).meta.public ? '/login' : path
    router.push(destination)
}
const items = computed(() => {
    const quickTitle = String(route.query.quickTitle || '')
    if (quickTitle.startsWith('quick_menu.')) {
        return [t(quickTitle)]
    }
    if (navigationStore.routePath === route.path && navigationStore.breadcrumb.length) {
        return navigationStore.breadcrumb
    }
    const fallbackItems = props.fallbackItems.filter(Boolean)
    // header 面包屑已有首页链接，去掉页面自带的首页项
    return inHeader && props.homePath && fallbackItems.length > 1 ? fallbackItems.slice(1) : fallbackItems
})
</script>

<style scoped>
.page-title-path {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    white-space: nowrap;
}

.page-title-item {
    overflow: hidden;
    color: #876563;
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    text-overflow: ellipsis;
}

.page-title-item.is-current {
    color: #111827;
    font-size: 15px;
    font-weight: 700;
}

.page-title-item.is-link:hover {
    color: #e94d4d;
}

.page-title-switch {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    font-size: 15px !important;
    font-weight: 700;
    font-family: inherit;
}

.page-title-switch .el-icon {
    font-size: 14px;
    color: #754b49;
}

.page-title-switch .el-icon :deep(svg) {
    stroke: currentColor;
    stroke-width: 1;
}

.page-title-switch:focus-visible {
    outline: 2px solid #d99590 !important;
    outline-offset: 3px;
}

:global(.page-title-dropdown .el-dropdown-menu) {
    max-height: 360px;
    overflow-y: auto;
    max-width: min(420px, 90vw);
}

:global(.page-title-dropdown .el-dropdown-menu__item.is-active) {
    color: #e94d4d;
    background: #fff0f0;
}

.page-title-separator {
    flex: 0 0 auto;
    padding: 0 9px;
    color: #c39a97;
    font-size: 15px;
    font-weight: 500;
    line-height: 1;
}
</style>
